import type { ThemeMode, UiThemePreset } from "./uiThemes";
import type { BookContentKind } from "./bookSpine";
import type { ChapterScene } from "./chapterScenes";

export type ChapterStatus = "draft" | "in_review" | "final";
export type { ThemeMode, UiThemePreset } from "./uiThemes";
export type { ChapterScene } from "./chapterScenes";
export type { PlotCell, PlotGridState, PlotThread } from "./plotGrid";
export type MeteringTier = "free" | "pro" | "enterprise";
export type PublishingPlatform = "kdp" | "ingramspark";
export type PublishingProfile = "print" | "ebook";

export type ChapterComment = {
  id: string;
  text: string;
  createdAt: string;
  /** Same slugs as web chapter comments (`note`, `question`, …). */
  type?: string;
  resolved?: boolean;
  /** `ai` = Story Craft / generated inline comments (legacy desktop + web). */
  createdBy?: "user" | "ai";
  /** Original highlighted passage at comment creation (DB `targetText`); not overwritten by edits. */
  targetText?: string;
  /** True when `targetText` no longer appears in the chapter body. */
  isStale?: boolean;
  /** Web reader / beta comment author (used when merging cloud comments into desktop). */
  authorUserId?: string;
  authorDisplayName?: string;
  /** Null/undefined = root. One-level replies only (matches web `parent_comment_id`). */
  parentCommentId?: string | null;
  /** TipTap paragraph/heading `attrs.id` (web `block_id`). */
  blockId?: string | null;
  anchor?: {
    from: number;
    to: number;
    /** Current text at the anchor (updates when the highlighted passage is edited). */
    excerpt?: string;
  };
};

export type ChapterTabData = {
  storyCraft: string;
  characters: string;
  locations: string;
  timeline: string;
  themesMotifs: string;
  summary: string;
};

export type ChapterSyncBase = {
  rev: number;
  title: string;
  content: string;
};

export type ChapterMetadata = {
  status: ChapterStatus;
  tags: string[];
  keywords: string[];
  label: string;
  customFields: Record<string, unknown>;
  /** Last server-acked snapshot for 3-way merge. */
  syncBase?: ChapterSyncBase;
};

export type ChapterVariationStatus = "open" | "applied" | "dismissed";

export type ChapterVariation = {
  id: string;
  createdAt: string;
  source: "chat" | "manual" | "storyCraft";
  prompt: string;
  /** TipTap JSON snapshot of the chapter when the variation was generated (legacy: plain text). */
  baseContent: string;
  /** TipTap JSON for the variation body with bold/italic/underline (legacy: plain text). */
  content: string;
  model?: string;
  /** Author-supplied purpose for creating this variation. */
  reason?: string;
  note?: string;
  status: ChapterVariationStatus;
};

export type Chapter = {
  id: string;
  title: string;
  /**
   * Optional caption under (or above) the chapter title — heading chrome, not body.
   * Plain text; use `\n` for multiple lines.
   */
  caption?: string;
  /** TipTap JSON document (stringified) — matches web `Chapter.content`. */
  content: string;
  /** Spine position (1-based). */
  order: number;
  /** Spine kind. Non-chapter kinds skip chapter numbering. */
  kind?: BookContentKind;
  /** Display chapter number among kind=chapter only; null for matter pages. */
  chapterNumber?: number | null;
  comments: ChapterComment[];
  notes: unknown[];
  tabData: ChapterTabData;
  metadata: ChapterMetadata;
  variations?: ChapterVariation[];
  /** Optional scene beats for planning (preferred over customFields.scenes). */
  scenes?: ChapterScene[];
  createdAt: string;
  updatedAt: string;
  /** Server content revision for optimistic concurrency. */
  rev?: number;
  /** Last-acked server rev this local chapter branched from. */
  baseRev?: number;
};

export type ConnectorStates = {
  googleDrive: boolean;
  oneDrive: boolean;
  dropbox: boolean;
};

export type PreflightCheck = {
  id: string;
  label: string;
  pass: boolean;
  detail?: string;
};

export type PreflightResult = {
  platform: PublishingPlatform;
  profile: PublishingProfile;
  pass: boolean;
  checks: PreflightCheck[];
  generatedAt: string;
};

export type RevisionSnapshot = {
  id: string;
  createdAt: string;
  note: string;
  wordCount: number;
  book: Book;
};

export type PlanningCard = {
  id: string;
  title: string;
  order: number;
  summary: string;
  status: ChapterStatus;
  tags: string[];
};

export type PendingConflict = {
  chapterId: string;
  chapterTitle: string;
  localExcerpt: string;
  remoteExcerpt: string;
  /** JSON of ThreeWayMergeResult for ConflictReview. */
  mergeJson?: string;
} | null;

export type Phase2State = {
  snapshots: RevisionSnapshot[];
  connectorStates: ConnectorStates;
  lastPreflight: PreflightResult | null;
  pendingConflict?: PendingConflict;
};

export type PluginManifest = {
  id: string;
  name: string;
  version: string;
  author: string;
  entrypoints: string[];
  permissions: string[];
  compatibilityRange: string;
};

export type PluginRecord = PluginManifest & {
  signed: boolean;
  signature: string | null;
  installedAt: string;
  mode: "sandbox-only" | "privileged-ready";
};

export type TokenMeteringEvent = {
  at: string;
  cost: number;
  allowed: boolean;
  remainingAfter?: number;
};

export type TokenMeteringState = {
  tier: MeteringTier;
  limit: number;
  used: number;
  history: TokenMeteringEvent[];
};

export type OAuthChecklistItem = {
  id: string;
  label: string;
  done: boolean;
};

export type LinkedOAuthAccount = {
  provider: string;
  providerUserId: string;
  linkedAt: string;
};

export type OAuthState = {
  enabled: boolean;
  linkedAccounts: LinkedOAuthAccount[];
  migrationChecklist: OAuthChecklistItem[];
};

export type McpApprovedTool = {
  toolName: string;
  scope: string;
  approvedAt: string;
};

export type McpCallAudit = {
  at: string;
  toolName: string;
  scope: string;
  approved: boolean;
};

export type McpState = {
  approvedTools: McpApprovedTool[];
  callAudit: McpCallAudit[];
};

export type ApiExtractionMilestone = {
  id: string;
  label: string;
  done: boolean;
};

export type AdvancedPublishingState = {
  selectedTemplate: string;
  lastChecklist: Array<{ id: string; label: string; done: boolean }>;
};

export type Phase3State = {
  plugins: PluginRecord[];
  tokenMetering: TokenMeteringState;
  oauth: OAuthState;
  mcp: McpState;
  apiExtraction: {
    milestones: ApiExtractionMilestone[];
  };
  advancedPublishing: AdvancedPublishingState;
};

export type Book = {
  id?: string;
  title: string;
  author: string;
  description: string;
  createdAt: string;
  updatedAt: string;
  settings: {
    theme: ThemeMode;
    /** 0 means no goal — footer hides progress. */
    writingGoalWords: number;
  };
  metadata: {
    language?: string;
    keywords?: string[];
    phase2?: Phase2State;
    phase3?: Phase3State;
    [key: string]: unknown;
  };
  chapters: Chapter[];
  feedbackEndpoint: string;
  phase2: Phase2State;
  phase3: Phase3State;
};

/** 0 = no writing goal. Rejects NaN/negative so the footer can hide when unset. */
export function normalizeWritingGoalWords(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(99_999_999, Math.round(n));
}

export type CompatContext = {
  passthroughFiles: Record<string, Buffer>;
};
